import {useCallback, useEffect, useRef, useState} from 'react'
import {View} from 'react-native'
import {Trans} from '@lingui/react/macro'

import {SESSION_BROKER_ALLOWED_ORIGINS} from '#/lib/constants'
import {logger} from '#/logger'
import {useAgent, useSession} from '#/state/session'
import {type SessionAccount} from '#/state/session/types'
import {isSessionExpired} from '#/state/session/util'
import {atoms as a, useTheme} from '#/alf'
import {Text} from '#/components/Typography'
import {
  type BrokeredSession,
  SESSION_BROKER_REQUEST,
  SESSION_BROKER_RESPONSE,
  type SessionBrokerRequest,
  type SessionBrokerResponse,
} from './protocol'

type Subscriber = {
  target: Window
  origin: string
}

function toBrokeredSession(
  account: SessionAccount | undefined,
): BrokeredSession | null {
  if (!account?.accessJwt || !account.handle) return null
  return {
    did: account.did,
    handle: account.handle,
    service: account.pdsUrl ?? account.service,
    accessJwt: account.accessJwt,
  }
}

/**
 * Hidden-iframe endpoint that hands the current session to other W Social
 * origins. See `README.md` in this directory for the embedding side.
 *
 * Runs inside the full app so the normal session machinery (resume on load,
 * cross-tab sync, refresh via the agent) keeps the tokens fresh; this screen
 * only relays them.
 *
 * Proactive pre-expiry refresh is owned by the session Provider
 * (src/state/session/index.tsx) — no duplicate timer here.
 */
export function SessionBrokerScreen() {
  const t = useTheme()
  const {currentAccount, isInitialLoad} = useSession()
  const agent = useAgent()
  const [subscriber, setSubscriber] = useState<Subscriber | null>(null)
  const [refreshFailed, setRefreshFailed] = useState(false)
  const refreshInFlight = useRef<Promise<void> | null>(null)
  const lastSent = useRef<{subscriber: Subscriber; accessJwt?: string} | null>(
    null,
  )

  const accessJwt = currentAccount?.accessJwt
  const expired = currentAccount ? isSessionExpired(currentAccount) : false

  const refresh = useCallback(() => {
    if (refreshInFlight.current) return refreshInFlight.current
    setRefreshFailed(false)
    refreshInFlight.current = agent.sessionManager
      .refreshSession()
      .then(() => undefined)
      .catch((e: unknown) => {
        logger.error('session-broker: failed to refresh session', {
          safeMessage: e instanceof Error ? e.message : String(e),
        })
        setRefreshFailed(true)
      })
      .finally(() => {
        refreshInFlight.current = null
      })
    return refreshInFlight.current
  }, [agent])

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!SESSION_BROKER_ALLOWED_ORIGINS.includes(event.origin)) return
      const data: unknown = event.data
      if (
        !data ||
        typeof data !== 'object' ||
        (data as Partial<SessionBrokerRequest>).type !== SESSION_BROKER_REQUEST
      ) {
        return
      }
      const source = event.source
      if (!source || !('postMessage' in source)) return
      // A fresh object per request so a re-request always gets a reply.
      setSubscriber({target: source as Window, origin: event.origin})
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  useEffect(() => {
    if (!subscriber || isInitialLoad) return

    if (currentAccount && expired && !refreshFailed) {
      // Token expired — trigger refresh; reply once new accessJwt arrives via session store.
      void refresh()
      return
    }

    if (
      lastSent.current?.subscriber === subscriber &&
      lastSent.current.accessJwt === accessJwt
    ) {
      return
    }

    const message: SessionBrokerResponse = {
      type: SESSION_BROKER_RESPONSE,
      session: expired ? null : toBrokeredSession(currentAccount),
    }
    if (refreshFailed) {
      message.error = 'refresh-failed'
    }
    subscriber.target.postMessage(message, subscriber.origin)
    lastSent.current = {subscriber, accessJwt}
  }, [
    subscriber,
    isInitialLoad,
    currentAccount,
    accessJwt,
    expired,
    refreshFailed,
    refresh,
  ])

  return (
    <View style={[a.flex_1, a.align_center, a.justify_center, a.p_lg]}>
      <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
        <Trans>
          This page shares your W Social login with other W Social sites.
        </Trans>
      </Text>
    </View>
  )
}
